import type { LangMessages } from '@pushwoosh/dumb-components/polyglot';

import type { PolyglotMessages } from './type';

// English is the source of truth. Every other language file should mirror these
// keys; until translated, the others re-export this file.
export const messages: LangMessages<PolyglotMessages> = {
  'LOGIN.TITLE': 'Sign in to {{ productName }}',
  'LOGIN.FIELD.USERNAME': 'Username or Email',
  'LOGIN.ERROR.INVALID_EMAIL': 'Please enter a valid email address',
  'LOGIN.FIELD.PASSWORD': 'Password',
  'LOGIN.ERROR.INVALID_PASSWORD': 'Please enter a valid password',
  'LOGIN.ERROR.PASSKEY_FAILED': 'Couldn’t sign in with a passkey. Try again or use your password.',
  'LOGIN.CHECKBOX.SHOW_PASSWORD': 'Show password',
  'LOGIN.LINK.FORGOT_PASSWORD': 'Forgot password?',
  'LOGIN.BUTTON.SIGN_IN': 'Sign in',
  'LOGIN.BUTTON.GOOGLE': 'Sign In With Google',
  'LOGIN.BUTTON.PASSKEY': 'Sign In With Passkey',
  'LOGIN.LOADING': 'Getting everything ready...',
  'LOGIN.FOOTER.NO_ACCOUNT': "Don't have an account?",
  'LOGIN.LINK.SIGN_UP': 'Sign up now',

  'REGISTER.TITLE': 'Start for free',
  'REGISTER.SUBTITLE':
    'All features available with a 1000-subscriber limit, ideal for testing and integration.',
  'REGISTER.FIELD.EMAIL': 'Business email',
  'REGISTER.ERROR.INVALID_EMAIL': 'Please enter a valid business email address',
  'REGISTER.PLACEHOLDER.LOADING': 'Loading...',
  'REGISTER.FIELD.FIRST_NAME': 'First name *',
  'REGISTER.ERROR.FIRST_NAME': 'Please enter your first name',
  'REGISTER.FIELD.LAST_NAME': 'Last name *',
  'REGISTER.ERROR.LAST_NAME': 'Please enter your last name',
  'REGISTER.FIELD.PHONE': 'Phone number',
  'REGISTER.ERROR.PHONE': 'Please enter a valid phone number',
  'REGISTER.ERROR.MUST_ACCEPT_TERMS': 'You must accept the terms to continue',
  'REGISTER.ERROR.CAPTCHA_FAILED': 'We couldn’t verify that you’re not a robot. Please try again or contact support.',
  'REGISTER.ERROR.GENERIC': 'Something went wrong. Please try again.',
  'REGISTER.AGREEMENT': 'I agree to the <a href="https://www.pushwoosh.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy</a> and <a href="https://www.pushwoosh.com/terms-of-use/" target="_blank" rel="noopener noreferrer">Terms</a>',
  'REGISTER.BUTTON.SIGN_UP': 'Sign up',
  'REGISTER.BUTTON.GOOGLE': 'Sign Up With Google',
  'REGISTER.LOADING': 'Getting everything ready...',
  'REGISTER.FOOTER.HAVE_ACCOUNT': 'Already have an account?',
  'REGISTER.LINK.SIGN_IN': 'Sign in',
  'REGISTER.SUCCESS.TITLE': 'Check Your Email',
  'REGISTER.SUCCESS.MESSAGE': 'You will receive a confirmation email at {{ email }} in a few minutes.',

  'CHANGE_PASSWORD.TITLE': 'Change Password',
  'CHANGE_PASSWORD.BANNER_TITLE': 'Please use a secure password',
  'CHANGE_PASSWORD.PASSWORD_HINT': 'Password must contain at least 8 characters',
  'CHANGE_PASSWORD.FIELD.NEW_PASSWORD': 'New Password',
  'CHANGE_PASSWORD.FIELD.CONFIRM_PASSWORD': 'Confirm Password',
  'CHANGE_PASSWORD.ERROR.REQUIRED': 'Please enter a password',
  'CHANGE_PASSWORD.ERROR.MIN_LENGTH': 'Password must contain at least 8 characters',
  'CHANGE_PASSWORD.ERROR.MISMATCH': 'Passwords do not match',
  'CHANGE_PASSWORD.BUTTON.SAVE': 'Save Password',
  'CHANGE_PASSWORD.INFO_NOTE':
    'For security reasons all active sessions of the account will be signed out after password reset.',
  'CHANGE_PASSWORD.LINK.SIGN_IN': 'Sign in',
  'CHANGE_PASSWORD.FOOTER.TO_ANOTHER_ACCOUNT': 'to another account',
  'CHANGE_PASSWORD.FOOTER.OR': 'or',
  'CHANGE_PASSWORD.LINK.SIGN_UP': 'Sign up',
  'CHANGE_PASSWORD.SUCCESS.TITLE': 'Password Recovery',
  'CHANGE_PASSWORD.SUCCESS.MESSAGE': 'Your password successfully changed.',

  'CREATE_PASSWORD.TITLE': 'Create a Password',
  'CREATE_PASSWORD.BANNER_TITLE': 'Please use a secure password',
  'CREATE_PASSWORD.PASSWORD_HINT': 'Password must contain at least 8 characters',
  'CREATE_PASSWORD.FIELD.PASSWORD': 'Password',
  'CREATE_PASSWORD.FIELD.REPEAT_PASSWORD': 'Repeat Password',
  'CREATE_PASSWORD.ERROR.REQUIRED': 'Please enter a password',
  'CREATE_PASSWORD.ERROR.MIN_LENGTH': 'Password must contain at least 8 characters',
  'CREATE_PASSWORD.ERROR.MISMATCH': 'Passwords do not match',
  'CREATE_PASSWORD.BUTTON.COMPLETE': 'Complete Registration',
  'CREATE_PASSWORD.CHECKBOX.MARKETING': 'I would like to receive service updates, news and announcements',
  'CREATE_PASSWORD.SUCCESS.TITLE': 'Account Created',
  'CREATE_PASSWORD.SUCCESS.MESSAGE': 'You have successfully registered.',
  'CREATE_PASSWORD.LINK.SIGN_IN': 'Sign in',

  'SELECT_ACCOUNT.TITLE': 'Choose Workspace',
  'SELECT_ACCOUNT.LOG_OUT': 'Log out',
  'SELECT_ACCOUNT.BUTTON.CONTINUE': 'Continue',
  'SELECT_ACCOUNT.TOOLTIP.MULTIUSER':
    'Multiuser login is not available on this account according to its current subscription plan.',

  'TWO_FACTOR.TITLE': 'Two-Factor Authentication',
  'TWO_FACTOR.SUBTITLE.AUTHENTICATOR':
    'To verify your identity, please enter the code from your Authenticator app.',
  'TWO_FACTOR.SUBTITLE.SENT':
    'To verify your identity, please enter the code we have sent to you via {{ method }}.',
  'TWO_FACTOR.FIELD.CODE': 'Confirmation Code',
  'TWO_FACTOR.ERROR.CODE': 'Please enter the verification code',
  'TWO_FACTOR.TRUST.LABEL': 'Trust this device',
  'TWO_FACTOR.TRUST.HINT': 'we will not ask for two-factor code for 30 days',
  'TWO_FACTOR.BUTTON.CONFIRM': 'Confirm',
  'TWO_FACTOR.BUTTON.RESEND': 'Resend code',
  'TWO_FACTOR.BUTTON.RESEND_WAIT':
    'Please wait {{ seconds }} {{ seconds | pluralize: "second", "seconds" }} to resend email...',
  'TWO_FACTOR.SUPPORT.QUESTION': 'Can’t access your account?',
  'TWO_FACTOR.LINK.OTHER_OPTIONS': 'Other options',
  'TWO_FACTOR.LINK.CONTACT_SUPPORT': 'Contact support',
  'TWO_FACTOR.LINK.LOG_OUT': 'Log out',

  'AUTH_OPTION.EMAIL': 'Email',
  'AUTH_OPTION.AUTHENTICATOR': 'Authentication app',
  'AUTH_OPTION.SMS': 'SMS',
  'AUTH_OPTION.WHATSAPP': 'WhatsApp',

  'AUTH_OPTIONS.TITLE': 'Authentication options',
  'AUTH_OPTIONS.LINK.CONTACT_SUPPORT': 'Contact support',
  'AUTH_OPTIONS.LINK.LOG_OUT': 'Log out',

  'INVITE.TITLE': 'Sign up to accept invite',
  'INVITE.SUBTITLE':
    'To access the invited workspace, please fill in the required fields for your <strong>{{ email }}</strong> login.',
  'INVITE.CREATE_PASSWORD.TITLE': 'Create a password',
  'INVITE.CREATE_PASSWORD.HINT':
    'Your password must include an uppercase letter, a lowercase letter, a number, and a special character.',
  'INVITE.FIELD.PASSWORD': 'Password',
  'INVITE.FIELD.REPEAT_PASSWORD': 'Repeat Password',
  'INVITE.ERROR.PASSWORD_REQUIRED': 'Please enter a password',
  'INVITE.ERROR.PASSWORD_MIN': 'Password must be at least 8 characters long',
  'INVITE.ERROR.CONFIRM_REQUIRED': 'Please repeat your password',
  'INVITE.ERROR.PASSWORDS_MUST_MATCH': 'Passwords must match',
  'INVITE.CHECKBOX.MARKETING': 'I would like to receive service updates, news and announcements',
  'INVITE.AGREEMENT': 'I agree to the <a href="https://www.pushwoosh.com/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy</a> and <a href="https://www.pushwoosh.com/terms-of-use/" target="_blank" rel="noopener noreferrer">Terms</a>',
  'INVITE.ERROR.MUST_ACCEPT_TERMS': 'You must accept the terms to continue',
  'INVITE.ERROR.ACCEPT_FAILED': 'Failed to accept the invitation',
  'INVITE.LOADING': 'Getting everything ready...',
  'INVITE.BUTTON.SIGN_UP': 'Sign up',
  'INVITE.BUTTON.GOOGLE': 'Sign Up With Google',

  'SETUP_PASSKEY.TITLE': 'Speed up next time?',
  'SETUP_PASSKEY.DESCRIPTION':
    'Add a passkey to sign in with your device biometrics (Touch ID, Face ID, Windows Hello) or a hardware security key — no more password typing.',
  'SETUP_PASSKEY.BUTTON.ADD': 'Add a passkey',
  'SETUP_PASSKEY.BUTTON.SKIP': 'Skip for now',
  'SETUP_PASSKEY.BUTTON.NEVER': "Don't ask again",

  'FORGOT_PASSWORD.TITLE': 'Password Recovery',
  'FORGOT_PASSWORD.FIELD.EMAIL': 'Email',
  'FORGOT_PASSWORD.ERROR.INVALID_EMAIL': 'Please enter a valid email address',
  'FORGOT_PASSWORD.RESULT.EMAIL_SENT':
    'If your email address exists in our database, you will receive a password recovery link at your email address in a few minutes',
  'FORGOT_PASSWORD.RESULT.ATTEMPTS_EXCEEDED':
    "You've exceeded the number of password reset attempts. Please try again in 1 hour.",
  'FORGOT_PASSWORD.BUTTON.SEND': 'Send Password Token',
  'FORGOT_PASSWORD.BUTTON.RESEND_WAIT':
    'Please wait {{ seconds }} {{ seconds | pluralize: "second", "seconds" }} to resend email...',
  'FORGOT_PASSWORD.LINK.SIGN_IN': 'Sign in',
  'FORGOT_PASSWORD.FOOTER.TO_ANOTHER_ACCOUNT': 'to another account',
  'FORGOT_PASSWORD.FOOTER.OR': 'or',
  'FORGOT_PASSWORD.LINK.SIGN_UP': 'Sign up',
  'SEPARATOR.OR': 'or',
};
